import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  Backdrop,
  CssBaseline,
} from "@mui/material";
import {
  Close as CloseIcon,
  Tag as TagIcon,
  Save as SaveIcon,
  Code as CodeIcon,
  AutoFixHigh as OptimizeIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const SnippetEditor = (props) => {
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [darkMode]
  );

  const [title, setTitle] = useState(props.snippet ? props.snippet.title : "");
  const [language, setLanguage] = useState(
    props.snippet ? props.snippet.language : ""
  );
  const [code, setCode] = useState(props.snippet ? props.snippet.code : "");
  const [tags, setTags] = useState(props.snippet ? props.snippet.tags : []);
  const [currentTag, setCurrentTag] = useState("");
  const [showOptimizedModal, setShowOptimizedModal] = useState(false);
  const [optimizationData, setOptimizationData] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Languages supported by Monaco Editor
  const supportedLanguages = [
    "javascript",
    "typescript",
    "python",
    "java",
    "c",
    "cpp",
    "csharp",
    "go",
    "rust",
    "ruby",
    "php",
    "swift",
    "kotlin",
    "sql",
    "html",
    "css",
  ];

  const handleAddTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleOptimizeCode = async () => {
    if (!code.trim()) return;
    setIsOptimizing(true);

    const data = {
      code: code,
    };

    const jsonData = JSON.stringify(data);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/optimized-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: jsonData,
        }
      );

      const responseData = await response.json();

      const optimizationData = {
        originalCode: code,
        optimizedCode: responseData.snippet.optimisedCode,
        originalTimeComplexity: responseData.snippet.originalTimeComplexity,
        optimizedTimeComplexity: responseData.snippet.optimisedTimeComplexity,
        originalSpaceComplexity: responseData.snippet.originalSpaceComplexity,
        optimizedSpaceComplexity: responseData.snippet.optimisedSpaceComplexity,
        reason: responseData.snippet.reason,
        suggestedTags: responseData.snippet.suggestedTags || [], // Get AI-generated tags
      };

      setOptimizationData(optimizationData);
      setShowOptimizedModal(true);
    } catch (error) {
      console.error("Error optimizing code:", error);
    } finally {
      setIsOptimizing(false);
    }
  };

  const useOptimizedCode = () => {
    if (optimizationData) {
      setCode(optimizationData.optimizedCode);
      setShowOptimizedModal(false);
    }
  };

  const useOptimizedTags = () => {
    if (
      optimizationData &&
      optimizationData.suggestedTags &&
      optimizationData.suggestedTags.length > 0
    ) {
      // Merge existing tags with AI-suggested tags (avoiding duplicates)
      const newTags = [...tags];
      optimizationData.suggestedTags.forEach((tag) => {
        if (!newTags.includes(tag)) {
          newTags.push(tag);
        }
      });
      setTags(newTags);

      // Disable the "Use These Tags" button
      setOptimizationData({
        ...optimizationData,
        suggestedTags: [], // Clear suggested tags to disable the button
      });
    }
  };

  const handleSaveSnippet = async () => {
    if (!title.trim() || !code.trim()) return;
    setIsSaving(true);

    try {
      await props.handleSubmit({
        title: title,
        language: language,
        code: code,
        tags: tags,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const editorContainerStyles = {
    "& .monaco-editor": {
      borderRadius: 1,
      overflow: "hidden",
      "& .overflow-guard": {
        borderRadius: 1,
      },
    },
  };

  const paperStyles = {
    p: { xs: 2, sm: 3, md: 4 },
    borderRadius: 2,
    boxShadow: (theme) => theme.shadows[3],
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: (theme) => theme.shadows[6],
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        <Paper elevation={3} sx={paperStyles}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
            }}
          >
            <CodeIcon sx={{ fontSize: "inherit" }} />
            {props.title}
          </Typography>

          <Box component="form" noValidate sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Title input */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="title"
                  label="Snippet Title"
                  name="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  variant="outlined"
                  placeholder="Enter a descriptive title"
                />
              </Grid>

              {/* Language selector */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="language-select-label">Language</InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={language}
                    label="Language"
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {supportedLanguages.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tags input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="tags"
                  label="Tags"
                  name="tags"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  placeholder="Add tags and press Enter"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={handleAddTag}
                          variant="contained"
                          size="small"
                          disabled={!currentTag.trim()}
                        >
                          Add
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Tags display */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Code editor */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" component="h2" gutterBottom>
                  Code
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    height: { xs: 300, sm: 400, md: 500 },
                    ...editorContainerStyles,
                  }}
                >
                  <Editor
                    height="100%"
                    defaultLanguage={language}
                    language={language}
                    value={code}
                    onChange={setCode}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: { xs: 12, sm: 14, md: 16 },
                      tabSize: 2,
                      automaticLayout: true,
                    }}
                  />
                </Paper>
              </Grid>

              {/* Action buttons */}
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={isOptimizing ? null : <OptimizeIcon />}
                    onClick={handleOptimizeCode}
                    disabled={!code.trim() || isOptimizing}
                    sx={{
                      flex: { sm: 1 },
                      position: "relative",
                    }}
                  >
                    {isOptimizing ? (
                      <>
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            marginLeft: "-12px",
                          }}
                        />
                        <span style={{ visibility: "hidden" }}>
                          Generate Optimized Code
                        </span>
                      </>
                    ) : (
                      "Generate Optimized Code"
                    )}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={isSaving ? null : <SaveIcon />}
                    onClick={handleSaveSnippet}
                    disabled={!title.trim() || !code.trim() || isSaving}
                    sx={{
                      flex: { sm: 1 },
                      position: "relative",
                    }}
                  >
                    {isSaving ? (
                      <>
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            marginLeft: "-12px",
                          }}
                        />
                        <span style={{ visibility: "hidden" }}>
                          Save Snippet
                        </span>
                      </>
                    ) : (
                      "Save Snippet"
                    )}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Loading backdrop for whole-page loading states (optional) */}
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isOptimizing || isSaving}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Optimized code modal */}
        <Dialog
          fullScreen={fullScreen}
          open={showOptimizedModal}
          onClose={() => setShowOptimizedModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            <Typography variant="h6">Optimized Code</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setShowOptimizedModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {optimizationData && (
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Original Time Complexity:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "grey.100" }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {optimizationData.originalTimeComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Optimized Time Complexity:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "success.light",
                        color: "success.contrastText",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {optimizationData.optimizedTimeComplexity}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Original Space Complexity:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "grey.100" }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {optimizationData.originalSpaceComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Optimized Space Complexity:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "success.light",
                        color: "success.contrastText",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {optimizationData.optimizedSpaceComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Optimization Reason:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: "background.default" }}
                  >
                    <Typography variant="body1">
                      {optimizationData.reason}
                    </Typography>
                  </Paper>
                </Box>

                {/* AI-Generated Tags Section */}
                {optimizationData.suggestedTags &&
                  optimizationData.suggestedTags.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        AI-Generated Tags:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, color: "info.contrastText" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {optimizationData.suggestedTags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              color="info"
                              variant="filled"
                            />
                          ))}
                        </Box>
                        <Button
                          variant="contained"
                          color="info"
                          size="small"
                          onClick={useOptimizedTags}
                          disabled={optimizationData.suggestedTags.length === 0}
                        >
                          Use These Tags
                        </Button>
                      </Paper>
                    </Box>
                  )}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Optimized Code:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ height: 300, overflow: "hidden" }}
                  >
                    <Editor
                      height="100%"
                      defaultLanguage={language}
                      language={language}
                      value={optimizationData.optimizedCode}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 14,
                        tabSize: 2,
                        automaticLayout: true,
                      }}
                    />
                  </Paper>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setShowOptimizedModal(false)}>Cancel</Button>
            <Button
              onClick={useOptimizedCode}
              variant="contained"
              color="primary"
            >
              Use Optimized Code
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default SnippetEditor;
